/** Contract of the five approved Marketing Scoreboard SQL batches. */
export interface ScoreField {
  seed_key?: string | null;
  journey_stage?: JourneyStage | null;
  id: string;
  label: string;
  value_type: 'count' | 'money' | 'rate';
  numerator_id: string | null;
  denominator_id: string | null;
  rate_format: 'percent' | 'money' | null;
  source_url: string | null;
  position: number;
  target: number | null;
  is_active: boolean;
}
export interface ScoreCell {
  value: number | null;
  entered_days: number;
}
export interface ScoreMetric extends ScoreField {
  totals: Partial<Record<'selected' | 'seven' | 'previous' | 'month' | 'prior', ScoreCell>>;
  change: number | null;
}
export interface ScoreSection {
  slug?: string;
  is_open?: boolean;
  is_manager?: boolean;
  journey?: Partial<Record<JourneyStage, ScoreJourneyCell>>;
  id: string;
  name: string;
  kind: 'daily' | 'snapshot';
  owner_id: string | null;
  owner_name: string | null;
  can_edit: boolean;
  entered_at: string | null;
  entered_fields: number;
  total_fields: number;
  metrics: ScoreMetric[];
}
export type JourneyStage = 'reached' | 'engaged' | 'hand' | 'call';
export interface ScoreJourneyCell {
  value: number | null;
  mapped_fields: number;
  ratio: number | null;
  paired_days: number;
}
export interface ScoreEntryLink {
  id: string;
  created_at: string;
  revoked_at: string | null;
  /** Who the link is for, typed when it was made. Null for older links. */
  person_name?: string | null;
}
export interface ScoreLoad {
  section_id: string;
  entry_date: string;
  version: number;
  fields_version: number;
  metrics: (ScoreField & { value: number | null })[];
}
export interface ScoreApi {
  totals(date: string): Promise<ScoreSection[]>;
  load(section: string, date: string): Promise<ScoreLoad>;
  save(snapshot: ScoreLoad, values: Record<string, number | null>): Promise<number>;
  editFields(snapshot: ScoreLoad, fields: ScoreField[]): Promise<number>;
  links(section: string): Promise<ScoreEntryLink[]>;
  createLink(section: string, person?: string): Promise<{ id: string; token: string }>;
  revokeLink(id: string): Promise<void>;
}

/** Explicit Manila calendar, including in browsers outside the Philippines. */
export function lastEntryDay(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Manila',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
  const today = Date.parse(`${part('year')}-${part('month')}-${part('day')}T00:00:00Z`);
  return new Date(today - 86_400_000).toISOString().slice(0, 10);
}
export function validEntryDay(value: string, max = lastEntryDay()): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    value <= max &&
    value >= '0001-01-01' &&
    !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) &&
    new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value
  );
}
export function rawFields(fields: ScoreField[]): ScoreField[] {
  return fields.filter((field) => field.is_active && field.value_type !== 'rate');
}
export function valuesFromDraft(fields: ScoreField[], draft: Record<string, string>) {
  return Object.fromEntries(
    rawFields(fields).map((field) => {
      const text = (draft[field.id] ?? '').trim();
      const value = text === '' ? null : Number(text);
      if (
        value !== null &&
        (!Number.isFinite(value) ||
          value < 0 ||
          (field.value_type === 'count' && !Number.isSafeInteger(value)))
      ) {
        throw new Error(
          `${field.label}: enter a non-negative ${field.value_type === 'count' ? 'whole number' : 'number'}, or leave blank.`,
        );
      }
      return [field.id, value];
    }),
  );
}
export function sourceLink(value: string | null): string | null {
  if (!value || !/^https:\/\/[^\s]+$/.test(value)) return null;
  try {
    return new URL(value).protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}
export function formatScore(
  value: number | null | undefined,
  field: Pick<ScoreField, 'value_type' | 'rate_format'>,
): string {
  if (value === null || value === undefined)
    return field.value_type === 'rate' ? 'Not available' : 'Not entered';
  if (field.value_type === 'money' || field.rate_format === 'money') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (field.value_type === 'rate')
    return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(
      value,
    );
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}
/** Strip server-only columns. score_edit_fields rejects unknown keys. */
export function editableField(field: ScoreField): ScoreField {
  const {
    id,
    label,
    value_type,
    numerator_id,
    denominator_id,
    rate_format,
    source_url,
    position,
    target,
    is_active,
    journey_stage,
  } = field;
  return {
    id,
    label,
    value_type,
    numerator_id,
    denominator_id,
    rate_format,
    source_url,
    position,
    target,
    is_active,
    journey_stage: journey_stage ?? null,
  };
}
export function validateFields(fields: ScoreField[], kind: ScoreSection['kind']): void {
  for (const field of fields) {
    if (!plainLabel(field.label).trim() || field.label.trim().length > 160)
      throw new Error('Every field needs a name of 1 to 160 characters.');
    if (field.source_url && !sourceLink(field.source_url))
      throw new Error(`${field.label}: use a valid https:// source link.`);
    if (field.target !== null && (!Number.isFinite(field.target) || field.target < 0))
      throw new Error(`${field.label}: target must be a non-negative number.`);
    if (kind === 'snapshot' && field.value_type !== 'count')
      throw new Error('Pipeline fields must be counts.');
    if (
      field.value_type === 'rate' &&
      (!field.rate_format ||
        [field.numerator_id, field.denominator_id].some(
          (id) => !fields.some((input) => input.id === id && input.value_type !== 'rate'),
        ))
    ) {
      throw new Error(`${field.label}: choose two raw fields from this section.`);
    }
  }
}
export function scoreError(error: unknown): string {
  const code = typeof error === 'object' && error !== null && 'code' in error ? error.code : null;
  if (code === '40001')
    return 'Someone changed this day or its fields. Your draft is kept. Copy anything you need, then reload and reconcile before saving.';
  if (code === '42501')
    return 'You no longer have permission to edit this section. Your draft is kept.';
  const message =
    typeof error === 'object' && error !== null && 'message' in error ? String(error.message) : '';
  if (message.includes('score_field_has_numbers'))
    return 'That field has saved numbers, so it can only be hidden. Nothing was saved; turn it off instead of deleting it.';
  if (message.includes('score_field_used_by_rate'))
    return 'A rate still uses that field. Change or delete the rate first. Nothing was saved.';
  if (message.includes('score_field_definition_immutable'))
    return 'A count or USD field cannot become a rate, or the other way round. Add a new field instead. Nothing was saved.';
  if (code === 'PGRST202' || code === '42883')
    return 'The Marketing Scoreboard database is not installed yet. No numbers have been saved.';
  return error instanceof Error
    ? error.message
    : 'The request could not be completed. Your draft is kept. Try again when connected.';
}

/** A field named "... (weekly)" is entered once a week, so it never counts toward a day being done. */
export function isWeekly(label: string): boolean {
  return /\(weekly\)\s*$/i.test(label);
}
/** The label without its "(weekly)" tag. Only the one space before the tag goes, so typing keeps trailing spaces. */
export function plainLabel(label: string): string {
  return label.replace(/ ?\(weekly\)\s*$/i, '');
}
/** A field name with its frequency: weekly fields carry the "(weekly)" tag, daily ones none. */
export function withFrequency(name: string, weekly: boolean): string {
  const plain = plainLabel(name);
  return weekly ? `${plain} (weekly)` : plain;
}
